import { keywords } from '../parser/keywords.js';
import { ClassInstanceField } from './models/classInstanceField.js';

const ignoreClassInstFields = [
  "length",
  "prototype",
  "name",
  keywords.title,
  keywords.only
];

/**
 * Extracts methods and properties from a class prototype and wraps them in `ClassInstanceField` instances.
 * This function traverses the inheritance chain to collect all inherited members.
 * 
 * @param groupName - The parent group name.
 * @param classInstance - The instance of the class to parse.
 * @returns An array of `ClassInstanceField` instances representing the class's members.
 */
export function parserClassInstFields(groupName: string, classInstance: any): ClassInstanceField[] {
  // get the class prototype
  const classProto = Reflect.getPrototypeOf(classInstance);

  // extract the class fields
  const fields = forEachClassProto(classProto)
    .filter(x => ignoreClassInstFields.includes(x as string) === false)
    .map(
      name => new ClassInstanceField(
        groupName,
        name as string,
        classProto![name as keyof typeof classProto],
        classInstance
      )
    );

  return fields;
}

/**
 * Traverses the class prototype chain to extract all property and method names.
 * 
 * @param classProto - The prototype of the class instance.
 * @returns An array of all inherited property and method keys.
 */
function forEachClassProto(classProto: object | null): Array<string | symbol> {
  const keys: Array<string | symbol> = [];
  const ignoreProtoFields = (x: string | symbol) => x !== 'constructor' && keys.indexOf(x) === -1;

  // iterate all class prototype fields (including class extended prototype fields)
  while (classProto && classProto !== Object.prototype) {
    keys.push(
      ...Reflect.ownKeys(classProto).filter(ignoreProtoFields)
    );
    classProto = Reflect.getPrototypeOf(classProto);
  }

  return keys;
}
